/**
 * Where the damage is, on one drawing.
 *
 * Review is a list, and a list is the wrong shape for the question an
 * estimator asks first: *is this a corner knock, or is the whole left side
 * gone?* Eleven rows do not answer that. A plan view does, at a glance, and it
 * is the same glance an adjuster takes.
 *
 * Drawn as a solid body with soft shadow and glowing damage rather than thin
 * line work: the first version read as a clip-art outline, which made the most
 * confident part of the screen look like the least considered. Weight and
 * depth also do real work — a lit zone reads as *hot*, which is exactly the
 * feeling "95% confident, structural" should carry.
 *
 * Colour carries the verdict, not the confidence — accepted is the only state
 * that means money. Pending glows amber and pulses faintly; nothing is claimed
 * until a human says so, and the drawing must not look decided before they
 * have decided.
 *
 * Zones the model invents are not drawn at all. There is no fallback shape: a
 * mystery panel lit up in the middle of the roof would be worse than one
 * missing from the picture, because it would be believed.
 */

import type { Verdict } from '../ai/review.js';

/** Body shell: long bonnet, cabin, boot. Generic on purpose. */
const BODY =
  'M75 4 C96 4 108 10 112 20 L118 44 C121 54 122 66 122 78 '
  + 'C122 96 121 112 118 126 L114 140 C110 147 96 150 75 150 '
  + 'C54 150 40 147 36 140 L32 126 C29 112 28 96 28 78 '
  + 'C28 66 29 54 32 44 L38 20 C42 10 54 4 75 4 Z';

/** Glass, read as one shape front and rear so the car has a direction. */
const GLASS_F = 'M52 34 C60 30 90 30 98 34 L104 52 C90 48 60 48 46 52 Z';
const GLASS_R = 'M46 112 C60 108 90 108 104 112 L98 128 C90 124 60 124 52 128 Z';
const ROOFLINE = 'M46 56 L104 56 L104 108 L46 108 Z';

const WHEELS = [
  'M22 40 h9 a3 3 0 0 1 3 3 v20 a3 3 0 0 1 -3 3 h-9 Z',
  'M128 40 h-9 a3 3 0 0 0 -3 3 v20 a3 3 0 0 0 3 3 h9 Z',
  'M22 96 h9 a3 3 0 0 1 3 3 v20 a3 3 0 0 1 -3 3 h-9 Z',
  'M128 96 h-9 a3 3 0 0 0 -3 3 v20 a3 3 0 0 0 3 3 h9 Z',
];

/**
 * Zone name -> shape, following the body curve so a lit panel looks part of
 * the car rather than a rectangle laid over it.
 *
 * Keys are what the model is asked to emit, plus the spellings it reaches for
 * anyway. An unmapped zone is skipped, never guessed.
 */
const ZONE: Record<string, string> = {
  front:        'M75 4 C96 4 108 10 112 20 L116 34 C100 28 50 28 34 34 L38 20 C42 10 54 4 75 4 Z',
  front_left:   'M75 4 C54 4 42 10 38 20 L34 34 C46 29 62 27 75 27 Z',
  front_right:  'M75 4 C96 4 108 10 112 20 L116 34 C104 29 88 27 75 27 Z',
  front_bumper: 'M75 4 C93 4 105 8 110 16 C96 11 54 11 40 16 C45 8 57 4 75 4 Z',
  grille:       'M56 8 C66 6 84 6 94 8 L96 18 C84 15 66 15 54 18 Z',
  bonnet:       'M36 32 C52 27 98 27 114 32 L117 52 C100 47 50 47 33 52 Z',
  hood:         'M36 32 C52 27 98 27 114 32 L117 52 C100 47 50 47 33 52 Z',
  left_side:    'M28 50 C28 50 30 100 34 128 L44 126 L44 52 Z',
  right_side:   'M122 50 C122 50 120 100 116 128 L106 126 L106 52 Z',
  left_front:   'M30 38 C29 46 28 60 28 74 L44 74 L44 40 Z',
  right_front:  'M120 38 C121 46 122 60 122 74 L106 74 L106 40 Z',
  left_rear:    'M28 80 C28 96 30 116 34 130 L44 128 L44 80 Z',
  right_rear:   'M122 80 C122 96 120 116 116 130 L106 128 L106 80 Z',
  roof:         ROOFLINE,
  interior:     ROOFLINE,
  rear:         'M75 150 C54 150 40 147 36 140 L33 126 C50 132 100 132 117 126 L114 140 C110 147 96 150 75 150 Z',
  rear_left:    'M75 150 C54 150 40 147 36 140 L33 126 C46 130 62 132 75 132 Z',
  rear_right:   'M75 150 C96 150 110 147 114 140 L117 126 C104 130 88 132 75 132 Z',
  rear_bumper:  'M75 150 C93 150 105 146 110 139 C96 143 54 143 40 139 C45 146 57 150 75 150 Z',
  rear_liftgate: 'M34 110 C50 105 100 105 116 110 L114 132 C98 127 52 127 36 132 Z',
  liftgate:     'M34 110 C50 105 100 105 116 110 L114 132 C98 127 52 127 36 132 Z',
  tailgate:     'M34 110 C50 105 100 105 116 110 L114 132 C98 127 52 127 36 132 Z',
  boot:         'M34 110 C50 105 100 105 116 110 L114 132 C98 127 52 127 36 132 Z',
};

export interface MapZone {
  zone: string;
  verdict: Verdict | 'pending';
  /** Highest confidence seen in this zone, for the tooltip. */
  confidence: number;
  /** What sits here, for the tooltip. */
  parts: string[];
}

/**
 * Collapse proposals to one entry per zone.
 *
 * Accepted wins over pending, and pending over rejected: the drawing shows the
 * strongest claim standing in each area. A zone with one accepted panel and
 * three rejected ones is an accepted zone.
 */
export function mapZones(
  items: Array<{ zone: string; partLabel: string; confidence: number;
                 verdict: Verdict | 'pending' }>,
): MapZone[] {
  const rank = { accepted: 3, pending: 2, dismissed: 1, rejected: 0 } as const;
  const byZone = new Map<string, MapZone>();
  for (const it of items) {
    const key = it.zone.toLowerCase();
    const seen = byZone.get(key);
    if (!seen) {
      byZone.set(key, { zone: key, verdict: it.verdict,
                        confidence: it.confidence, parts: [it.partLabel] });
      continue;
    }
    seen.parts.push(it.partLabel);
    seen.confidence = Math.max(seen.confidence, it.confidence);
    const a = rank[seen.verdict as keyof typeof rank] ?? 0;
    const b = rank[it.verdict as keyof typeof rank] ?? 0;
    if (b > a) seen.verdict = it.verdict;
  }
  return [...byZone.values()];
}

export function DamageMap({ zones }: { zones: MapZone[] }) {
  const drawn = zones.filter((z) => ZONE[z.zone]);
  const unmapped = zones.length - drawn.length;
  const hot = drawn.filter((z) => z.verdict !== 'rejected').length;

  return (
    <figure className="dmap">
      <div className="dmaphead">
        <span className="dmaptitle">Damage map</span>
        <span className="dmapcount mono">
          {hot} {hot === 1 ? 'area' : 'areas'}
        </span>
      </div>

      <svg viewBox="0 0 150 156" role="img"
           aria-label={drawn.length
             ? `Damage map: ${drawn.map((z) =>
                 `${z.zone.replace(/_/g, ' ')} ${z.verdict}`).join(', ')}`
             : 'Damage map: nothing marked yet'}>
        <defs>
          {/* Depth, so the shell reads as a body and not an outline. */}
          <linearGradient id="dm-shell" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--dm-shell-a)" />
            <stop offset="100%" stopColor="var(--dm-shell-b)" />
          </linearGradient>
          <filter id="dm-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" /><feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="dm-drop" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow dx="0" dy="1.6" stdDeviation="2" floodOpacity="0.16" />
          </filter>
        </defs>

        {WHEELS.map((d, i) => <path key={i} d={d} className="dm-wheel" />)}

        <path d={BODY} className="dm-shell" filter="url(#dm-drop)" />
        <path d={ROOFLINE} className="dm-roof" />
        <path d={GLASS_F} className="dm-glass" />
        <path d={GLASS_R} className="dm-glass" />

        {/* Damage last, so it sits above the body it belongs to. */}
        {drawn.map((z) => (
          <path key={z.zone} d={ZONE[z.zone]} className={`dm-zone ${z.verdict}`}
                filter={z.verdict === 'rejected' ? undefined : 'url(#dm-glow)'}>
            <title>
              {z.zone.replace(/_/g, ' ')} — {z.verdict} ({z.confidence}%)
              {'\n'}{z.parts.join(', ')}
            </title>
          </path>
        ))}

        <text x="75" y="155" className="dm-cap" textAnchor="middle">FRONT AT TOP</text>
      </svg>

      <figcaption>
        <span><i className="dm-key acc" />accepted</span>
        <span><i className="dm-key pen" />awaiting you</span>
        <span><i className="dm-key rej" />rejected</span>
        {/* Said out loud rather than hidden: a zone the drawing cannot place is
            still on the estimate, and an estimator who trusted the picture
            alone would miss it. */}
        {unmapped > 0 && (
          <span className="dm-more">
            {unmapped} not on this drawing — see the list
          </span>
        )}
      </figcaption>
    </figure>
  );
}
